import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import { Toast } from "primereact/toast";
import { paymentLinkService } from "../../../services/paymentLinkService";
import { useParams } from "react-router-dom";
import { Button } from "primereact/button";

const POLL_INTERVAL_MS = 5_000;
const POLL_TIMEOUT_MS  = 300_000;

type PageState =
  | "loading"
  | "form"
  | "submitting"
  | "success"
  | "failed"
  | "expired"
  | "paid"
  | "processing"
  | "error";

const WacepayLogo = () => (
  <div className="flex items-center gap-2 mb-6">
    <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center">
      <span className="text-white text-xs font-bold">W</span>
    </div>
    <span className="font-semibold text-gray-700 text-sm">Secured by WACEPAY</span>
    <span className="ml-auto">
      <i className="pi pi-lock text-gray-400 text-xs" />
    </span>
  </div>
);

const StatusScreen = ({
  icon,
  iconClass,
  title,
  message,
  sub,
  action,
}: {
  icon: string;
  iconClass: string;
  title: string;
  message: string;
  sub?: string;
  action?: React.ReactNode;
}) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
    <div className="bg-white rounded-2xl shadow-lg w-full max-w-sm p-8 text-center">
      <WacepayLogo />
      <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${iconClass}`}>
        <i className={`${icon} text-2xl`} />
      </div>
      <h2 className="text-xl font-bold text-gray-800 mb-2">{title}</h2>
      <p className="text-gray-500 text-sm mb-2">{message}</p>
      {sub && <p className="text-gray-400 text-xs mb-6">{sub}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  </div>
);

const Spinner = ({ message }: { message: string }) => (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center z-50">
    <div className="w-14 h-14 border-4 border-white/20 border-t-primary-500 rounded-full animate-spin mb-6" />
    <p className="text-white font-medium text-sm">{message}</p>
    <p className="text-white/50 text-xs mt-2">Please do not close this page</p>
    <p className="text-white/30 text-xs absolute bottom-8">🔒 Secured by WACEPAY</p>
  </div>
);

const PaymentLink: React.FC = () => {
  const { id_link } = useParams<{ id_link: string }>();
  const toastRef    = useRef<Toast>(null);

  const [pageState, setPageState]   = useState<PageState>("loading");
  const [paymentData, setPaymentData] = useState<any>(null);
  const [selected, setSelected]     = useState<string | null>(null);
  const [errorMsg, setErrorMsg]     = useState<string>("");

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef  = useRef<ReturnType<typeof setTimeout>  | null>(null);

  const stopPolling = () => {
    if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
    if (pollTimeoutRef.current)  { clearTimeout(pollTimeoutRef.current);  pollTimeoutRef.current  = null; }
  };

  useEffect(() => () => stopPolling(), []);

  const fetchPaymentLink = async () => {
    setPageState("loading");

    if (!id_link) {
      setPageState("error");
      setErrorMsg("This payment link is invalid.");
      return;
    }

    try {
      const res = await paymentLinkService.initPayment(id_link);

      // Lien expiré
      if (res?.code === "PAYMENT_LINK_EXPIRED" || res?.status === 410) {
        setPageState("expired");
        return;
      }

      // Lien déjà payé
      if (res?.status === 409 && res?.data?.status === "PAID") {
        setPageState("paid");
        return;
      }

      // Paiement déjà en cours
      if (res?.status === 409 && res?.data?.status === "PROCESSING") {
        setPageState("processing");
        return;
      }

      if (res?.status !== 2000 || !res?.data) {
        setPageState("error");
        setErrorMsg("This payment link is unavailable. Please contact the merchant.");
        return;
      }

      setPaymentData(res.data);
      setPageState("form");
    } catch {
      setPageState("error");
      setErrorMsg("Unable to load payment details. Please check your connection and try again.");
    }
  };

  useEffect(() => { fetchPaymentLink(); }, []);

  const startPollingStatus = () => {
    pollTimeoutRef.current = setTimeout(() => {
      stopPolling();
      // Après 5 min sans confirmation, on laisse l'utilisateur dans un état neutre
      setPageState("form");
      toastRef.current?.show({
        severity: "warn",
        summary: "No confirmation yet",
        detail: "We couldn't confirm your payment in time. If you were debited, contact the merchant.",
        life: 8000,
      });
    }, POLL_TIMEOUT_MS);

    pollIntervalRef.current = setInterval(async () => {
      try {
        const res = await paymentLinkService.PaymentLinkCheckStatus(id_link!);
        const txStatus = res?.data?.transaction?.status;

        if (txStatus === "SUCCESS") {
          stopPolling();
          setPageState("success");
          return;
        }

        if (txStatus === "FAILED" || txStatus === "EXPIRED" || txStatus === "CANCELED" || txStatus === "ABANDONED") {
          stopPolling();
          setPageState("failed");
          return;
        }
        // PENDING / INITIATED → on continue le polling
      } catch {
        // Erreur réseau sur le check — on continue silencieusement, le timeout global gèrera
      }
    }, POLL_INTERVAL_MS);
  };

  const handleSelect = async (operator: { code: string; id: string }) => {
    setSelected(operator.code);
    await formik.setFieldValue("serviceId", operator.id, false);
    await formik.setFieldValue("selected", operator.code, false);
    await formik.setFieldTouched("selected", true, false);
    await formik.setFieldTouched("serviceId", true, false);
    await formik.validateField("selected");
    await formik.validateField("serviceId");
  };

  const formik = useFormik({
    initialValues: { serviceId: "", phoneNumber: "", selected: "" },
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: Yup.object({
      serviceId:   Yup.string().required("Please select a payment method"),
      phoneNumber: Yup.string()
        .required("Phone number is required")
        .matches(/^\+\d{6,15}$/, "Enter a valid international phone number"),
      selected:    Yup.string().required("Please select a payment method"),
    }),
    onSubmit: async (values) => {
      setPageState("submitting");

      try {
        const res = await paymentLinkService.initPaymentLinkTransaction({
          serviceId:   values.serviceId,
          phoneNumber: values.phoneNumber,
          linkId:      id_link!,
        });

        if (res?.code === "PAYMENT_LINK_EXPIRED" || res?.status === 410) {
          setPageState("expired");
          return;
        }

        if (res?.status === 409) {
          setPageState(res?.data?.status === "PAID" ? "paid" : "processing");
          return;
        }

        if (res?.status !== 2000) {
          // Erreur métier connue — on revient au formulaire avec un message sobre
          setPageState("form");
          toastRef.current?.show({
            severity: "warn",
            summary: "Payment not initiated",
            detail: "We could not process your payment. Please verify your details and try again.",
            life: 5000,
          });
          return;
        }

        // Succès de l'initiation → polling du statut
        startPollingStatus();
      } catch {
        setPageState("form");
        toastRef.current?.show({
          severity: "error",
          summary: "Connection error",
          detail: "A network error occurred. Please try again.",
          life: 5000,
        });
      }
    },
  });

  // ── Écrans d'état ───────────────────────────────────────────────────────────

  if (pageState === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-primary-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Loading payment details…</p>
        </div>
      </div>
    );
  }

  if (pageState === "error") {
    return (
      <StatusScreen
        icon="pi pi-exclamation-circle"
        iconClass="bg-red-100 text-red-500"
        title="Link unavailable"
        message={errorMsg || "This payment link is not available."}
        action={
          <Button
            label="Try again"
            icon="pi pi-refresh"
            className="p-button-outlined"
            onClick={fetchPaymentLink}
          />
        }
      />
    );
  }

  if (pageState === "expired") {
    return (
      <StatusScreen
        icon="pi pi-clock"
        iconClass="bg-orange-100 text-orange-500"
        title="Link expired"
        message="This payment link is no longer valid."
        sub="Payment links expire after 15 minutes. Contact the merchant to get a new link."
      />
    );
  }

  if (pageState === "paid") {
    return (
      <StatusScreen
        icon="pi pi-check-circle"
        iconClass="bg-green-100 text-green-600"
        title="Already paid"
        message="This payment has already been completed successfully."
        sub="You do not need to pay again."
      />
    );
  }

  if (pageState === "processing") {
    return (
      <StatusScreen
        icon="pi pi-spin pi-spinner"
        iconClass="bg-blue-100 text-blue-500"
        title="Payment in progress"
        message="A payment is currently being processed for this link."
        sub="Please wait a moment and refresh the page to check the status."
        action={
          <Button
            label="Refresh"
            icon="pi pi-refresh"
            className="p-button-outlined"
            onClick={fetchPaymentLink}
          />
        }
      />
    );
  }

  if (pageState === "success") {
    return (
      <StatusScreen
        icon="pi pi-check-circle"
        iconClass="bg-green-100 text-green-600"
        title="Payment successful"
        message={`Your payment of ${paymentData?.amount} ${paymentData?.currency} to ${paymentData?.merchantName} was completed.`}
        sub="You will receive a confirmation shortly."
      />
    );
  }

  if (pageState === "failed") {
    return (
      <StatusScreen
        icon="pi pi-times-circle"
        iconClass="bg-red-100 text-red-500"
        title="Payment failed"
        message="Your payment could not be completed."
        sub="Please try again or use a different payment method."
        action={
          <Button
            label="Try again"
            icon="pi pi-refresh"
            onClick={() => {
              formik.resetForm();
              setSelected(null);
              setPageState("form");
            }}
          />
        }
      />
    );
  }

  // ── Formulaire principal ────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-8">
      <Toast ref={toastRef} position="top-center" />
      {pageState === "submitting" && <Spinner message="Processing your payment…" />}

      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-6">
          <WacepayLogo />
          <h1 className="text-2xl font-bold text-gray-800">
            Pay {paymentData?.merchantName}
          </h1>
          {paymentData?.reason && (
            <p className="text-gray-500 text-sm mt-1">{paymentData.reason}</p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-5">
          {/* Carte récapitulatif */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 flex-1 p-6">
            <h2 className="font-semibold text-gray-700 mb-4 text-sm uppercase tracking-wide">
              Payment summary
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-50">
                <span className="text-gray-500 text-sm">Merchant</span>
                <span className="font-medium text-gray-800 text-sm">{paymentData?.merchantName}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-50">
                <span className="text-gray-500 text-sm">Amount</span>
                <span className="font-bold text-gray-800 text-lg">
                  {paymentData?.amount} <span className="text-sm font-normal text-gray-500">{paymentData?.currency}</span>
                </span>
              </div>
              {paymentData?.reason && (
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-500 text-sm">Reason</span>
                  <span className="text-gray-700 text-sm text-right max-w-[180px]">{paymentData.reason}</span>
                </div>
              )}
            </div>
            <div className="mt-6 bg-gray-50 rounded-xl p-3 flex items-center gap-2">
              <i className="pi pi-lock text-green-500 text-xs" />
              <span className="text-xs text-gray-400">256-bit encrypted transaction</span>
            </div>
          </div>

          {/* Carte formulaire */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 flex-1 p-6">
            <h2 className="font-semibold text-gray-700 mb-4 text-sm uppercase tracking-wide">
              Payment method
            </h2>

            <form onSubmit={formik.handleSubmit} className="space-y-5">
              {/* Sélection opérateur */}
              <div>
                <p className="text-sm text-gray-500 mb-3">Select your mobile money operator</p>
                {paymentData?.services?.length === 0 ? (
                  <div className="text-center py-6 text-gray-400 text-sm">
                    <i className="pi pi-info-circle block text-2xl mb-2" />
                    No payment method available for this link.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-3">
                    {paymentData?.services?.map((operator: any) => (
                      <button
                        key={operator.id}
                        type="button"
                        onClick={() => handleSelect(operator)}
                        className={`w-20 h-20 rounded-xl border-2 overflow-hidden transition-all focus:outline-none ${
                          selected === operator.code
                            ? "border-primary-600 ring-2 ring-primary-200 shadow-md"
                            : "border-gray-200 hover:border-gray-400"
                        }`}
                      >
                        <img
                          src={`/assets/images/services/${operator.code}.png`}
                          alt={operator.name}
                          className="object-cover w-full h-full"
                        />
                      </button>
                    ))}
                  </div>
                )}
                {formik.errors.selected && formik.touched.selected && (
                  <p className="text-red-500 text-xs mt-2">{formik.errors.selected}</p>
                )}
              </div>

              {/* Numéro de téléphone */}
              <div>
                <p className="text-sm text-gray-500 mb-2">Your mobile money number</p>
                <PhoneInput
                  placeholder="Enter phone number"
                  international
                  countryCallingCodeEditable={false}
                  id="phoneNumber"
                  name="phoneNumber"
                  defaultCountry={(paymentData?.customerCountry || "CM").toUpperCase()}
                  countries={[(paymentData?.customerCountry || "CM").toUpperCase()]}
                  className={`w-full p-2 border rounded-xl text-sm ${
                    formik.errors.phoneNumber && formik.touched.phoneNumber
                      ? "border-red-400 bg-red-50"
                      : "border-gray-200"
                  }`}
                  value={formik.values.phoneNumber}
                  onBlur={formik.handleBlur}
                  onChange={(phone) => {
                    formik.setFieldValue("phoneNumber", phone || "", true);
                    formik.setFieldTouched("phoneNumber", true, true);
                  }}
                />
                {formik.errors.phoneNumber && formik.touched.phoneNumber && (
                  <p className="text-red-500 text-xs mt-1">{formik.errors.phoneNumber}</p>
                )}
              </div>

              {/* Bouton */}
              <Button
                type="submit"
                loading={pageState === "submitting"}
                disabled={
                  pageState === "submitting" ||
                  !formik.isValid ||
                  !formik.values.serviceId ||
                  !formik.values.phoneNumber
                }
                className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-xl font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                label={pageState === "submitting" ? "Processing…" : `Pay ${paymentData?.amount} ${paymentData?.currency}`}
                icon={pageState !== "submitting" ? "pi pi-send" : undefined}
              />
            </form>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          This link expires 15 minutes after creation · Powered by WACEPAY
        </p>
      </div>
    </div>
  );
};

export default PaymentLink;
